import React, { useEffect, useState, useContext } from "react";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";
import { formatPhone } from "../../utils/formatPhone";

const CustomerList = ({ onEdit, refreshTrigger }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [error, setError] = useState(null);

  const apiBase = getApiBaseURL();
  const { user } = useContext(AuthContext);
  const isPrimary = user?.is_primary === true;

  const filteredCustomers = search
    ? customers.filter(
        (c) =>
          c.name?.toLowerCase().includes(search.toLowerCase()) ||
          c.email?.toLowerCase().includes(search.toLowerCase())
      )
    : customers;

  useEffect(() => {
    fetchCustomers();
  }, [refreshTrigger]);

  const fetchCustomers = async () => {
    setLoading(true);
    setError(null);

    try {
      // HIPAA Compliant: Using secureAxios with httpOnly cookies
      const response = await secureAxios.get(`${apiBase}/api/customers`);
      setCustomers(response.data);
    } catch (err) {
      console.error("❌ Error loading customers:", err);
      setError("Failed to load customers. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (customerId, customerName) => {
    if (
      !window.confirm(
        `Are you sure you want to delete ${customerName}?\n\nThis action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await secureAxios.delete(`${apiBase}/api/customers/${customerId}`);
      // Remove from local state
      setCustomers((prev) => prev.filter((c) => c.customer_id !== customerId));
      alert("✅ Customer deleted successfully!");
    } catch (err) {
      console.error("❌ Failed to delete customer:", err);
      alert(err.response?.data?.error || "Failed to delete customer");
    }
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading customers...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
        <button
          className="btn btn-sm btn-outline-danger ms-3"
          onClick={fetchCustomers}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="mb-3">
      <div className="row mb-3">
        <div className="col-md-6">
          <label htmlFor="search" className="form-label">
            Search Customers
          </label>
          <input
            type="text"
            id="search"
            className="form-control"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="col-md-6 d-flex align-items-end">
          <span className="text-muted">
            Showing {filteredCustomers.length} of {customers.length} customers
          </span>
        </div>
      </div>

      {/* Table view for desktop/tablet */}
      <div className="table-responsive d-none d-md-block">
        <table className="table table-hover">
          <thead className="table-light">
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Status</th>
              <th>Phone</th>
              <th>City</th>
              <th>Organization</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center text-muted">
                  {search
                    ? "No customers found matching your search."
                    : "No customers found."}
                </td>
              </tr>
            ) : (
              filteredCustomers.map((c) => (
                <tr key={c.customer_id}>
                  <td>
                    <strong>{c.name}</strong>
                  </td>
                  <td>{c.email}</td>
                  <td>
                    <span className="badge bg-secondary">
                      {c.status || "Active"}
                    </span>
                  </td>
                  <td>{formatPhone(c.phone_number)}</td>
                  <td>{c.address?.city || "—"}</td>
                  <td>{c.organization_type || "—"}</td>
                  <td className="text-end">
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={() => onEdit(c)}
                      title="Edit customer"
                    >
                      <i className="bi bi-pencil"></i> Edit
                    </button>
                    {/* <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(c.customer_id, c.name)}
                      title="Delete customer"
                    >
                      <i className="bi bi-trash"></i> Delete
                    </button> */}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile-friendly card view */}
      <div className="d-block d-md-none">
        {filteredCustomers.length === 0 ? (
          <div className="alert alert-info">
            {search
              ? "No customers found matching your search."
              : "No customers found."}
          </div>
        ) : (
          filteredCustomers.map((c) => (
            <div key={c.customer_id} className="card mb-3">
              <div className="card-body">
                <h5 className="card-title">
                  {c.name}
                  <span className="badge bg-secondary ms-2">
                    {c.status || "Active"}
                  </span>
                </h5>
                <div className="mb-2">
                  <strong>Email:</strong> {c.email}
                  <br />
                  <strong>Phone:</strong> {formatPhone(c.phone_number)}
                  <br />
                  <strong>City:</strong> {c.address?.city || "—"}
                  <br />
                  <strong>Organization:</strong> {c.organization_type || "—"}
                </div>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => onEdit(c)}
                  >
                    <i className="bi bi-pencil"></i> Edit
                  </button>
                  {/* <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDelete(c.customer_id, c.name)}
                  >
                    <i className="bi bi-trash"></i> Delete
                  </button> */}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* PHI Notice */}
      <div className="mt-3">
        <small className="text-muted">
          <i className="bi bi-shield-lock"></i> This list contains Protected
          Health Information (PHI). All access is logged for HIPAA compliance.
        </small>
      </div>
    </div>
  );
};

export default CustomerList;
